import { SiteOwnerSettingsSource } from "../@types"
import bounded from "./bounded"
import {
  createMeasurementsConfig,
  MeasurementsConfig,
} from "./measurementsConfig"
import { PageSettings } from "./pageSettings"
import { assertNumeric } from "./sessionConfigValidation"

export interface SiteOwnerSettings {
  applicationEventsBeaconIntervalInMilliseconds: number
  /**
   * The maximum number of test objects to measure initially. It might also
   * make sense to use this to limit the size of batches submitted by the
   * measurement scheduler.
   */
  maxInitialTestObjects: number
  measurementStartDelay: number
  /**
   * Time to wait between measurement scheduler checks, when any measurements
   * _due_ are scheduled to be run as soon as possible. Lower values allow
   * measurements to be more spread out. Higher values cause more batching.
   */
  schedulerRunIntervalInMilliseconds: number
  /**
   * The time to wait between session config downloads.
   */
  sessionRefreshIntervalInMilliseconds: number
  /**
   * Settings that apply to measurement-taking generally
   */
  measurementsConfig: MeasurementsConfig
}

export const createSiteOwnerSettings: (
  siteOwnerSettingsSource: SiteOwnerSettingsSource,
  pageSettings: PageSettings,
) => SiteOwnerSettings = (s, p) => new SiteOwnerSettingsImpl(s, p)

class SiteOwnerSettingsImpl implements SiteOwnerSettings {
  private _applicationEventsBeaconIntervalInMilliseconds
  private _maxInitialTestObjects
  private _measurementStartDelay
  private _measurementsConfig
  private _schedulerRunIntervalInMilliseconds
  private _sessionRefreshIntervalInMilliseconds
  constructor(
    siteOwnerSettingsSource: SiteOwnerSettingsSource,
    pageSettings: PageSettings,
  ) {
    /* eslint-disable @typescript-eslint/dot-notation */
    // Add some safety rails for values read from session configuration
    let tempApplicationEventsInterval =
      siteOwnerSettingsSource["application_events_interval"]
    tempApplicationEventsInterval =
      typeof tempApplicationEventsInterval === "number"
        ? tempApplicationEventsInterval
        : 1
    tempApplicationEventsInterval = bounded(
      tempApplicationEventsInterval,
      0,
      60,
    )
    this._applicationEventsBeaconIntervalInMilliseconds = minutesToMilliseconds(
      tempApplicationEventsInterval,
      pageSettings.applicationEventsBeaconInterval,
    )
    const tempMaxInitialTestObjects =
      siteOwnerSettingsSource["max_initial_test_objects"]
    assertNumeric("max_initial_test_objects", tempMaxInitialTestObjects)
    this._maxInitialTestObjects = bounded(tempMaxInitialTestObjects, 5, 20)
    const tempMeasurementStartDelay =
      siteOwnerSettingsSource["measurement_start_delay"]
    assertNumeric("measurement_start_delay", tempMeasurementStartDelay)
    this._measurementStartDelay = bounded(tempMeasurementStartDelay, 0, 60)
    let tempSessionRefreshInterval =
      siteOwnerSettingsSource["session_refresh_interval"]
    assertNumeric("session_refresh_interval", tempSessionRefreshInterval)
    tempSessionRefreshInterval = bounded(tempSessionRefreshInterval, 0.5, 60)
    this._sessionRefreshIntervalInMilliseconds = minutesToMilliseconds(
      tempSessionRefreshInterval,
      pageSettings.sessionRefreshInterval,
    )
    let tempSchedulerRunIntervalInSeconds =
      siteOwnerSettingsSource["scheduler_run_interval"]
    assertNumeric("scheduler_run_interval", tempSchedulerRunIntervalInSeconds)
    tempSchedulerRunIntervalInSeconds = bounded(
      tempSchedulerRunIntervalInSeconds,
      10,
      120,
    )
    this._schedulerRunIntervalInMilliseconds = secondsToMilliseconds(
      tempSchedulerRunIntervalInSeconds,
      pageSettings.schedulerRunInterval,
    )
    this._measurementsConfig = createMeasurementsConfig(
      siteOwnerSettingsSource["measurements"] || {},
    )
    /* eslint-enable @typescript-eslint/dot-notation */
  }

  get applicationEventsBeaconIntervalInMilliseconds() {
    return this._applicationEventsBeaconIntervalInMilliseconds
  }

  get maxInitialTestObjects() {
    return this._maxInitialTestObjects
  }

  get measurementsConfig() {
    return this._measurementsConfig
  }

  get measurementStartDelay() {
    return this._measurementStartDelay
  }

  get sessionRefreshIntervalInMilliseconds() {
    return this._sessionRefreshIntervalInMilliseconds
  }

  get schedulerRunIntervalInMilliseconds() {
    return this._schedulerRunIntervalInMilliseconds
  }
}

function minutesToMilliseconds(
  sessionConfigSetting: number,
  pageLevelSetting?: number,
) {
  return 60 * secondsToMilliseconds(sessionConfigSetting, pageLevelSetting)
}

function secondsToMilliseconds(
  sessionConfigSetting: number,
  pageLevelSetting?: number,
) {
  const setting =
    typeof pageLevelSetting === "number"
      ? pageLevelSetting
      : sessionConfigSetting
  return setting * 1000
}
